import { PieceJointeBar } from '@mairistem/comptabilite';
import { Fiche, useCreateFiche } from '@mairistem/fiche';
import {
	FichePanelComplements,
	Fichiers,
	getNatureFromGabarit,
	PANEL_TYPE,
	TYPE_GABARIT,
	useGabaritPj,
} from '@mairistem/finances-utils';
import { isNewIdentifiant, newIdentifiant } from '@mairistem/tools';
import { isNil } from 'lodash';
import React from 'react';
import BaseProfilEcritureProjet from '@/src/classes/ProfilEcriture/BaseProfilEcritureProjet';
import Commentaires from '@/src/components/Common/Commentaires';
import EcrituresComptables from '@/src/components/Common/EcrituresComptables/EcrituresComptables';
import Financement from '@/src/components/ProjetFichePanel/Financement/Financement';
import ProjetInformation from '@/src/components/ProjetFichePanel/ProjetInformation/ProjetInformation';
import ProjetListeContainer from '@/src/components/ProjetListe/ProjetListeContainer';
import { ProjetStatus } from '@/src/constants/projetStatus';
import type { Composant } from '@/src/interfaces/composant';
import type { Projet } from '@/src/interfaces/projet';

interface ProjetFicheConsumerProps {
	identifiant: string | number;
	projet?: Projet;
	errors?: any;
	onChange: any;
}

const DEFAULT_COMPOSANTS = [{ identifiant: newIdentifiant() }];

const ProjetFicheConsumer = ({
	identifiant,
	projet,
	errors = null,
	onChange,
	...rest
}: ProjetFicheConsumerProps) => {
	const {
		libelle,
		montantHT,
		montantTVA,
		montantTTC,
		dateFin,
		identifiantCodeTVA,
		etiquettes,
		identifiantEntite = parseInt(
			sessionStorage.getItem('identifiantEntite'),
			10,
		),
		dateCreation,
		dateExecution,
		status = ProjetStatus.CONCEPTION,
		identifiantDocInfo,
		referenceInterne,
		utilisateursProjet,
		deletedUtilisateursProjet,
		composants = DEFAULT_COMPOSANTS,
		deletedComposants,
		deletedDocuments,
		identifiantService,
	} = projet || {};

	const createFiche = useCreateFiche({
		path: '/subventionsPercues',
	});

	const [profilEcriture, setProfilEcriture] = React.useState(
		new BaseProfilEcritureProjet(),
	);
	const [, gabaritsPj] = useGabaritPj([TYPE_GABARIT.PROJET]);

	const handleChangePj = React.useCallback(
		(changes) => {
			onChange({
				identifiant,
				...changes,
			});
		},
		[identifiant, onChange],
	);
	const handleCreateSubvention = React.useCallback(() => {
		createFiche({
			identifiantProjet: identifiant,
			identifiantEntite: parseInt(
				sessionStorage.getItem('identifiantEntite'),
				10,
			),
			realisationHT: 1,
			composants,
			projet,
		});
	}, [createFiche, identifiant, composants, projet]);

	const handleDeletePj = React.useCallback(
		(identifiantPj: number) => {
			onChange?.({
				identifiant,
				deletedDocuments: [...(deletedDocuments || []), identifiantPj],
			});
		},
		[identifiant, onChange, deletedDocuments],
	);

	const information = React.useMemo(
		() => (
			<Fiche.Panel key="general" id="general" title="Général">
				<ProjetInformation
					projet={{
						identifiant,
						libelle,
						montantHT,
						montantTVA,
						montantTTC,
						dateFin,
						identifiantCodeTVA,
						etiquettes,
						identifiantEntite,
						dateCreation,
						dateExecution,
						status,
						identifiantDocInfo,
						referenceInterne,
						utilisateursProjet,
						deletedUtilisateursProjet,
						composants: composants as Composant[],
						deletedComposants,
						identifiantService,
					}}
					onChange={onChange}
					errors={errors}
				/>
			</Fiche.Panel>
		),
		[
			libelle,
			montantHT,
			montantTVA,
			montantTTC,
			dateFin,
			identifiantCodeTVA,
			errors,
			identifiant,
			onChange,
			etiquettes,
			identifiantEntite,
			dateCreation,
			dateExecution,
			status,
			identifiantDocInfo,
			referenceInterne,
			utilisateursProjet,
			deletedUtilisateursProjet,
			composants,
			deletedComposants,
			identifiantService,
		],
	);

	const financement = React.useMemo(
		() => (
			<Fiche.Panel
				key="financement"
				id="financement"
				title="PLAN DE FINANCEMENT"
			>
				<Financement
					projet={projet}
					onCreateSubvention={handleCreateSubvention}
				/>
			</Fiche.Panel>
		),
		[projet, handleCreateSubvention],
	);

	const ecrituresComptables = React.useMemo(
		() => (
			<Fiche.Panel key="ecritures" id="ecritures" title="ECRITURES COMPTABLES">
				<EcrituresComptables
					projet={projet}
					conditionsVersements={projet?.subventions?.flatMap(
						(s) => s.conditionsVersements,
					)}
					montantSubvention={projet?.subventions?.reduce(
						(acc, current) => acc + current.mntAttribue,
						0,
					)}
					realisationHT
					montantProjet={projet?.montantHT ?? 0}
				/>
			</Fiche.Panel>
		),
		[projet],
	);

	const voletPolitique = React.useMemo(
		() => (
			<Fiche.Panel
				key="voletPolitique"
				id="voletPolitique"
				title="volet politique"
			>
				<FichePanelComplements
					codeProfilEcriture="PRO"
					data={projet}
					handleProfilEcritureChange={setProfilEcriture}
					identifiantEntite={identifiantEntite}
					identifiantFiche={identifiant}
					onChange={onChange}
					panelType={PANEL_TYPE.SUBVENTION}
					profilEcriture={profilEcriture}
				/>
			</Fiche.Panel>
		),
		[onChange, identifiant, identifiantEntite, profilEcriture, projet],
	);

	const commentaires = React.useMemo(
		() => (
			<Fiche.Panel key="commentaires" id="commentaires" title="COMMENTAIRES">
				<Commentaires
					identifiant={projet?.identifiant}
					commentaire={projet?.commentaire}
					infosPubliques={projet?.infosPubliques}
					onChange={onChange}
				/>
			</Fiche.Panel>
		),
		[
			projet?.identifiant,
			onChange,
			projet?.commentaire,
			projet?.infosPubliques,
		],
	);

	const getPjSideBar = React.useCallback(
		() => (
			<PieceJointeBar
				identifiantFichierLie={identifiant}
				nomFichierLie={Fichiers.PROJET}
				types={getNatureFromGabarit(
					gabaritsPj?.find((g) => g.type === TYPE_GABARIT.PROJET),
				)}
				onChange={handleChangePj}
				onDeletePj={handleDeletePj}
				typeGabarit={TYPE_GABARIT.PROJET}
			/>
		),
		[identifiant, handleChangePj, handleDeletePj, gabaritsPj],
	);

	let header = null;
	if (isNil(identifiant) || isNewIdentifiant(identifiant)) {
		header = 'Nouveau projet';
	} else {
		header = `Projet ${libelle}`;
	}

	const sidebars = React.useMemo(
		() => ({
			left: <ProjetListeContainer />,
			right: {
				content: getPjSideBar(),
				icon: 'attach',
				alwaysPinned: true,
				width: '15vw',
			},
		}),
		[getPjSideBar],
	);

	return (
		<span className="ficheWithSideBar">
			<Fiche
				key="ficheProjet"
				type="projet"
				header={header}
				errors={errors}
				sidebars={sidebars}
				{...rest}
			>
				{information}
				{financement}
				{ecrituresComptables}
				{voletPolitique}
				{commentaires}
			</Fiche>
		</span>
	);
};

export default ProjetFicheConsumer;
